import {
  Collapse,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {KeyboardArrowDown, KeyboardArrowUp} from '@material-ui/icons';
import {isLoaded} from 'Core/models/Async';
import {
  RateAggregateClassTier,
  RateAggregatePlanCoverage,
  RateAggregatePlanCoverageClass,
  RateAggregatePolicyPlan,
  RatePremiumDetails,
  TIER_LOOK_UP,
  AgeBandRateCalcType,
} from 'Quoting/models/DTOs/RatePremium';
import {A360ProductCd, A360ProductModel} from 'Quoting/models/Quoting';
import {
  VALUE_NOT_AVAILABLE,
  formatPremium,
  isA360HealthProduct,
  isA360LifeProduct,
  isDefined,
  parsePath,
  safeRenderValue,
} from 'Quoting/utils/utilities';
import {flatten} from 'fp-ts/lib/Array';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {Fragment, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getRateAggregatePolicy, hasRateAggregateCalculated} from './RatePremiumTableUtils';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {List as ImmutableList, Map as ImmutableMap} from 'immutable';
import {GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP} from '../Plan/PlanTemplates/GWL/gpluConstants';
import classNames from 'classnames';

const useRateCardStyles = makeStyles(() => ({
  rateCardPlanName: {
    paddingLeft: '1rem',
    fontSize: '1.25rem',
    fontWeight: 600,
    padding: '2rem',
  },
  rateCardPlanPremium: {
    backgroundColor: '#F4F8FC',
    borderTopRightRadius: '0.625rem',
    padding: '1.5rem',
    '& > :nth-child(1)': {
      fontSize: '1.625rem',
      fontWeight: 600,
    },
    '& > :nth-child(2)': {
      fontSize: '1rem',
    },
  },
  planRatesContainer: {
    border: '1px solid #ccc',
    borderRadius: '10px',
    margin: '1.5rem 0',
  },
  coverageListHeader: {
    backgroundColor: '#F1F1F1',
  },
  coverageListItem: {
    backgroundColor: '#F4F8FC',
    borderBottomRightRadius: '0.625rem',
    borderBottomLeftRadius: '0.625rem',
  },
  textAlignRight: {
    textAlign: 'right',
  },
  textAlignLeft: {
    textAlign: 'left',
  },
  tierNameCell: {
    paddingLeft: '3.75rem',
  },
  expandCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  ageBandExpandCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: '3.75rem',
  },
  hidden: {
    visibility: 'hidden',
  },
}));

export const RatePremiumTable = ({
  quoteRatesAndPremium,
  quoteStatus,
  productModel,
}: {
  quoteRatesAndPremium: RatePremiumDetails | undefined;
  quoteStatus: string | undefined;
  productModel?: string;
}) => {
  const rateAggregatePolicy = getRateAggregatePolicy(quoteRatesAndPremium?.rateAggregates);

  return isDefined(quoteRatesAndPremium) &&
    isDefined(rateAggregatePolicy) &&
    Array.isArray(rateAggregatePolicy.plans) &&
    hasRateAggregateCalculated(rateAggregatePolicy, quoteStatus) ? (
    <Grid>
      {rateAggregatePolicy.plans.map(plan => (
        <RatePremiumPlanList plan={plan} key={plan._key?.id} productModel={productModel} />
      ))}
    </Grid>
  ) : null;
};

export const RatePremiumPlanList = ({plan, productModel}: {plan: RateAggregatePolicyPlan; productModel?: string}) => {
  const classes = useRateCardStyles();
  return (
    <div className={classes.planRatesContainer}>
      <Premium
        plan={plan}
        annualPremium={plan.rateCardPremium?.annualPremium?.amount}
        monthlyPremium={plan.rateCardPremium?.monthlyPremium?.amount}
        productModel={productModel}
      />
      <List component="ul" className={classes.coverageListHeader}>
        <ListItem>
          <Grid
            container
            justifyContent={isA360HealthProduct(productModel as A360ProductModel) ? 'space-around' : 'flex-start'}
          >
            <Grid item xs={3}>
              Coverage
            </Grid>
            <Grid item xs={3} className={classes.textAlignRight}>
              {isA360HealthProduct(productModel as A360ProductModel) && productModel === A360ProductModel.CIMaster && (
                <>{productModel === A360ProductModel.CIMaster ? 'Benefit Amount' : ''}</>
              )}
            </Grid>
            <Grid item xs={2} className={classes.textAlignRight}>
              Payroll Frequency Rate
            </Grid>
            {isA360HealthProduct(productModel as A360ProductModel) && (
              <>
                <Grid item xs={2} className={classes.textAlignRight}>
                  Payroll Frequency Premium
                </Grid>
                <Grid item xs={2} className={classes.textAlignRight}>
                  Annual Premium
                </Grid>
              </>
            )}
          </Grid>
        </ListItem>
      </List>
      <Divider style={{width: '100%', listStyle: 'none'}} component="li" />
      {Array.isArray(plan?.coverages) && (
        <List component="ul" className={classes.coverageListItem}>
          {plan?.coverages?.map(coverage => (
            <RatePremiumCoverageList
              coverage={coverage}
              planName={plan.planName}
              key={coverage._key?.id}
              productModel={productModel}
            />
          ))}
        </List>
      )}
    </div>
  );
};

type RatePremiumCoverageListProps = {
  coverage: RateAggregatePlanCoverage;
  planName: string;
  productModel: string;
};

const RatePremiumCoverageList = (props: RatePremiumCoverageListProps) => {
  const {plansWithCoverages} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();
  const classes = useRateCardStyles();
  const coverage = props.coverage;
  const quotePlans = getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans');
  const quotePlan: ImmutableMap<string, any> = quotePlans?.find(plan => plan.get('planName') === props.planName);

  const benefitAmount = isDefined(quotePlan)
    ? (quotePlan?.getIn(parsePath('coverageDefs[0].benefitStructures[0].benefitAmount.amount')) as number)
    : 0;

  const coverageName = useMemo(() => {
    if (isLoaded(plansWithCoverages)) {
      return pipe(
        plansWithCoverages.Data,
        plans => plans.map(p => p.coverages),
        flatten,
        coverages => coverages.find(cov => cov.code === coverage.coverageCode || cov.code === coverage.coverageType),
        coverageFromPlanOptions =>
          isA360HealthProduct(props.productModel as A360ProductModel)
            ? coverageFromPlanOptions?.name
            : getCoverageName(coverageFromPlanOptions.code) ?? coverageFromPlanOptions?.name ?? VALUE_NOT_AVAILABLE
      );
    }
    return VALUE_NOT_AVAILABLE;
  }, [coverage, plansWithCoverages]);

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Fragment>
      <ListItem>
        <Grid container alignItems="center">
          <Grid item xs={3} className={classes.expandCell}>
            <ListItemIcon>
              <IconButton
                onClick={() => setIsExpanded(prev => !prev)}
                size="small"
                aria-label={`${isExpanded ? 'collapse' : 'expand'} rate details for coverage ${coverageName}`}
              >
                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </ListItemIcon>
            <Typography>{coverageName}</Typography>
          </Grid>
          <Grid item xs={3} className={classes.textAlignRight}>
            {isA360HealthProduct(props.productModel as A360ProductModel) &&
              props.productModel === A360ProductModel.CIMaster && (
                <Typography>
                  {props.productModel === A360ProductModel.CIMaster ? formatPremium(benefitAmount) : ''}
                </Typography>
              )}
          </Grid>
          <Grid item xs={2} className={classes.textAlignRight}>
            {VALUE_NOT_AVAILABLE}
          </Grid>
          {isA360HealthProduct(props.productModel as A360ProductModel) && (
            <>
              <Grid item xs={2} className={classes.textAlignRight}>
                <Typography>{formatPremium(coverage?.rateCardPremium?.payrollFrequencyPremium?.amount)}</Typography>
              </Grid>
              <Grid item xs={2} className={classes.textAlignRight}>
                <Typography>{formatPremium(coverage?.rateCardPremium?.annualPremium?.amount)}</Typography>
              </Grid>{' '}
            </>
          )}
        </Grid>
      </ListItem>
      <Divider style={{width: '100%', listStyle: 'none'}} component="li" />
      <Collapse in={isExpanded} unmountOnExit component="li">
        <RatePremiumCoverageClassList
          coverageClasses={coverage.classes}
          coverageCode={coverage.coverageCode}
          productModel={props.productModel}
        />
      </Collapse>
    </Fragment>
  );
};

type RatePremiumCoverageClassListProps = {
  coverageClasses: RateAggregatePlanCoverageClass[];
  coverageCode: string;
  productModel: string;
};

const RatePremiumCoverageClassList = (props: RatePremiumCoverageClassListProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const classes = useRateCardStyles();
  const {coverageClasses, coverageCode} = props;

  return Array.isArray(coverageClasses) ? (
    <List component="ul" style={{backgroundColor: '#fff'}}>
      {coverageClasses.map(coverageClass => (
        <Fragment key={coverageClass?._key?.id}>
          <ListItem>
            <Grid container alignItems="center">
              <Grid item xs={3} className={classes.expandCell}>
                <ListItemIcon>
                  <IconButton
                    onClick={() => setIsExpanded(prev => !prev)}
                    size="small"
                    aria-label={`${isExpanded ? 'collapse' : 'expand'} rate details for coverage ${
                      coverageClass.className
                    }`}
                    className={classNames({
                      [classes.hidden]:
                        Array.isArray(coverageClass?.ageBandedRateCards) &&
                        coverageClass?.ageBandedRateCards?.length === 0,
                    })}
                  >
                    {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  </IconButton>
                </ListItemIcon>
                <Typography>{safeRenderValue(coverageClass.className)}</Typography>
              </Grid>
              <Grid item xs={3} className={classes.textAlignRight}>
                {isA360HealthProduct(props.productModel as A360ProductModel) &&
                  props.productModel === A360ProductModel.CIMaster && <></>}
              </Grid>
              <Grid item xs={2} className={classes.textAlignRight}>
                {isA360HealthProduct(props.productModel as A360ProductModel) ||
                (Array.isArray(coverageClass?.ageBandedRateCards) && coverageClass?.ageBandedRateCards?.length > 0)
                  ? VALUE_NOT_AVAILABLE
                  : coverageClass?.rateCard?.payrollFrequencyRate}
              </Grid>
              {isA360HealthProduct(props.productModel as A360ProductModel) && (
                <>
                  <Grid item xs={2} className={classes.textAlignRight}>
                    <Typography>
                      {formatPremium(coverageClass?.rateCardPremium?.payrollFrequencyPremium?.amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} className={classes.textAlignRight}>
                    <Typography>{formatPremium(coverageClass?.rateCardPremium?.annualPremium?.amount)}</Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </ListItem>
          <Collapse in={isExpanded} unmountOnExit component="li">
            {isA360HealthProduct(props.productModel as A360ProductModel) && (
              <RatePremiumCoverageClassTierList tiers={coverageClass.tiers} coverageCode={coverageCode} />
            )}
            {isA360LifeProduct(props.productModel as A360ProductModel) &&
              Array.isArray(coverageClass?.ageBandedRateCards) &&
              coverageClass?.ageBandedRateCards?.length > 0 && (
                <List component="ul">
                  {coverageClass?.ageBandedRateCards?.map(ageBandRate => (
                    <Fragment key={ageBandRate?._key?.id}>
                      <ListItem>
                        <Grid container alignItems="center">
                          <Grid item xs={3} className={classes.tierNameCell}>
                            <Typography>{`${ageBandRate?.ageBand}, ${ageBandRate?.tobacco}`}</Typography>
                          </Grid>
                          <Grid item xs={3} className={classes.textAlignRight} />
                          <Grid item xs={2} className={classes.textAlignRight}>
                            <Typography>{ageBandRate?.rateCard?.payrollFrequencyRate}</Typography>
                          </Grid>
                        </Grid>
                      </ListItem>
                      <Divider style={{width: '100%', listStyle: 'none'}} component="li" />
                    </Fragment>
                  ))}
                </List>
              )}
          </Collapse>
        </Fragment>
      ))}
    </List>
  ) : null;
};

type RateAggregateClassTierProps = {
  tiers: RateAggregateClassTier[];
  coverageCode: string;
};

type AgeBandProps = {
  tier: RateAggregateClassTier;
};

const AgeBandDropDown = (props: AgeBandProps) => {
  const classes = useRateCardStyles();
  const {tier} = props;
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <Fragment key={tier?._key?.id}>
      <ListItem>
        <Grid container alignItems="center">
          <Grid item xs={3} className={classes.ageBandExpandCell}>
            <ListItemIcon>
              <IconButton
                onClick={() => setIsExpanded(prev => !prev)}
                size="small"
                aria-label={`${isExpanded ? 'collapse' : 'expand'} rate details for tier ${TIER_LOOK_UP[tier.tier]}`}
              >
                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </ListItemIcon>
            <Typography>{TIER_LOOK_UP[tier.tier]}</Typography>
          </Grid>
          <Grid item xs={3} className={classes.textAlignRight} />
          <Grid item xs={2} className={classes.textAlignRight}>
            {VALUE_NOT_AVAILABLE}
          </Grid>
          <Grid item xs={2} className={classes.textAlignRight}>
            <Typography>{formatPremium(tier.rateCardPremium?.payrollFrequencyPremium?.amount)}</Typography>
          </Grid>
          <Grid item xs={2} className={classes.textAlignRight}>
            <Typography>{formatPremium(tier.rateCardPremium?.annualPremium?.amount)}</Typography>
          </Grid>
        </Grid>
      </ListItem>
      <Divider style={{width: '100%', listStyle: 'none'}} component="li" />
      <Collapse in={isExpanded} unmountOnExit component="li">
        <CIRatePremiumCoverageClassTierList ageBandRateList={sortAgeBandsByTobacco(tier.ageBandRateCalc)} />
      </Collapse>
    </Fragment>
  );
};

const RatePremiumCoverageClassTierList = (props: RateAggregateClassTierProps) => {
  const classes = useRateCardStyles();
  const {tiers, coverageCode} = props;
  switch (coverageCode) {
    case A360ProductCd.HI:
      return Array.isArray(tiers) ? (
        <List component="ul">
          {tiers.map(tier => (
            <Fragment key={tier?._key?.id}>
              <ListItem>
                <Grid container alignItems="center">
                  <Grid item xs={3} className={classes.tierNameCell}>
                    <Typography>{TIER_LOOK_UP[tier.tier]}</Typography>
                  </Grid>
                  <Grid item xs={3} className={classes.textAlignRight} />
                  <Grid item xs={2} className={classes.textAlignRight}>
                    <Typography>
                      {formatPremium(tier.rateCardPremium?.participantPaidPayrollFrequencyRate?.amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} className={classes.textAlignRight}>
                    <Typography>{formatPremium(tier.rateCardPremium?.payrollFrequencyPremium?.amount)}</Typography>
                  </Grid>
                  <Grid item xs={2} className={classes.textAlignRight}>
                    <Typography>{formatPremium(tier.rateCardPremium?.annualPremium?.amount)}</Typography>
                  </Grid>
                </Grid>
              </ListItem>
              <Divider style={{width: '100%', listStyle: 'none'}} component="li" />
            </Fragment>
          ))}
        </List>
      ) : (
        <></>
      );
    case A360ProductCd.CI:
      return Array.isArray(tiers) ? (
        <List component="ul">
          {tiers.map(tier => (
            <AgeBandDropDown tier={tier} key={tier?._key?.id} />
          ))}
        </List>
      ) : (
        <></>
      );
    case A360ProductCd.ACC:
      return Array.isArray(tiers) ? (
        <List component="ul">
          {tiers.map(tier => (
            <Fragment key={tier?._key?.id}>
              <ListItem>
                <Grid container alignItems="center">
                  <Grid item xs={3} className={classes.tierNameCell}>
                    <Typography>{TIER_LOOK_UP[tier.tier]}</Typography>
                  </Grid>
                  <Grid item xs={3} className={classes.textAlignRight} />
                  <Grid item xs={2} className={classes.textAlignRight}>
                    <Typography>{formatPremium(tier.rateCardPremium?.payrollFrequencyRate?.amount)}</Typography>
                  </Grid>
                  <Grid item xs={2} className={classes.textAlignRight}>
                    <Typography>{formatPremium(tier.rateCardPremium?.payrollFrequencyPremium?.amount)}</Typography>
                  </Grid>
                  <Grid item xs={2} className={classes.textAlignRight}>
                    <Typography>{formatPremium(tier.rateCardPremium?.annualPremium?.amount)}</Typography>
                  </Grid>
                </Grid>
              </ListItem>
              <Divider style={{width: '100%', listStyle: 'none'}} component="li" />
            </Fragment>
          ))}
        </List>
      ) : (
        <></>
      );

    default:
      return <></>;
  }
};

const sortAgeBandsByTobacco = (ageBandRateList: AgeBandRateCalcType[]) => {
  let tobaccoList: AgeBandRateCalcType[] = [];
  let nonTobaccoList: AgeBandRateCalcType[] = [];
  if (Array.isArray(ageBandRateList)) {
    tobaccoList = ageBandRateList.filter(ageBand => ageBand.tobaccoClass == 'Tobacco');
    nonTobaccoList = ageBandRateList.filter(ageBand => ageBand.tobaccoClass == 'NonTobacco');
    const sortedList = nonTobaccoList.concat(tobaccoList);
    return sortedList;
  } else {
    return null;
  }
};

const CIRatePremiumCoverageClassTierList = ({ageBandRateList}: {ageBandRateList: AgeBandRateCalcType[]}) => {
  const classes = useRateCardStyles();

  return Array.isArray(ageBandRateList) ? (
    <List component="ul" style={{backgroundColor: '#fff'}}>
      {ageBandRateList.map(ageBandRate => (
        <Fragment key={ageBandRate?._key?.id}>
          <ListItem>
            <Grid container alignItems="center">
              <Grid item xs={3} className={classes.textAlignLeft} style={{paddingLeft: '8rem'}}>
                <Typography>
                  {ageBandRate.ageBand} {ageBandRate.tobaccoClass === 'Tobacco' ? 'Tobacco' : 'Non-Tobacco'}
                </Typography>
              </Grid>
              <Grid item xs={3} className={classes.textAlignRight}>
                {''}
              </Grid>
              <Grid item xs={2} className={classes.textAlignRight}>
                <Typography>{formatPremium(ageBandRate.rateCardPremium?.payrollFrequencyRate?.amount)}</Typography>
              </Grid>
              <Grid item xs={2} className={classes.textAlignRight}>
                <Typography>{formatPremium(ageBandRate?.rateCardPremium?.payrollFrequencyPremium?.amount)}</Typography>
              </Grid>
              <Grid item xs={2} className={classes.textAlignRight}>
                <Typography>{formatPremium(ageBandRate?.rateCardPremium?.annualPremium?.amount)}</Typography>
              </Grid>
            </Grid>
          </ListItem>
          <Divider style={{width: '100%', listStyle: 'none'}} component="li" />
        </Fragment>
      ))}
    </List>
  ) : null;
};

const Premium = (props: {
  plan: RateAggregatePolicyPlan;
  annualPremium?: number;
  monthlyPremium?: number;
  productModel?: string;
}) => {
  const classes = useRateCardStyles();
  return (
    <Fragment>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item className={classes.rateCardPlanName}>
          {safeRenderValue(props.plan?.planName)}
        </Grid>
        {isA360HealthProduct(props.productModel as A360ProductModel) && (
          <Grid item>
            <Grid container direction="column" className={classes.rateCardPlanPremium}>
              <Grid item>{formatPremium(props.monthlyPremium, 'monthly')}</Grid>
              <Grid item>{formatPremium(props.annualPremium, 'annual')}</Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Divider style={{width: '100%', listStyle: 'none'}} component="li" />
    </Fragment>
  );
};

export const RatePremiumTableView = () => {
  const {quoteRatesAndPremium, quote} = useSelector((state: ReducerState) => state.quoteForm);
  return (
    <RatePremiumTable
      quoteRatesAndPremium={quoteRatesAndPremium}
      quoteStatus={isLoaded(quote) ? quote.Data.get('state') : undefined}
      productModel={isLoaded(quote) ? quote.Data.get('_modelName') : undefined}
    />
  );
};

const getCoverageName = (coverageCode: string) => GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP[coverageCode];
